import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Row, Col, Breadcrumb, message, Spin, Empty, Tag, Tooltip,
    Progress, Button, Space, Typography, Alert
} from "antd";
import {
    HomeOutlined, GlobalOutlined, AppstoreOutlined, BuildOutlined,
    ExperimentOutlined, DashboardOutlined, ReloadOutlined, ShoppingOutlined,
    ContainerOutlined, DollarOutlined, ThunderboltOutlined, ToolOutlined,
    ArrowRightOutlined, ClockCircleOutlined, CheckCircleOutlined,
    AlertOutlined, SafetyCertificateOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Doughnut, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend,
    CategoryScale, LinearScale, BarElement, Title
} from "chart.js";
import dayjs from "dayjs";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import api from "../../services/axiosInstance";
import { fmtMoney } from "../../utils/reportExport";
import "../../styles/fluidDesign.css";

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const { Content } = Layout;

// ── Modern palette ──────────────────────────────────────────────────────────
const PALETTE = ["#45a186", "#ffa000", "#3b82f6", "#8b5cf6", "#f43f5e", "#14b8a6", "#f97316", "#0ea5e9"];
const STATUS_COLORS = {
    growing: "#45a186",
    flowering: "#ffa000",
    fruiting: "#3b82f6",
    harvested: "#8b5cf6",
    diseased: "#f43f5e",
    sick: "#f43f5e",
    dead: "#94a3b8",
    harvested_done: "#14b8a6",
};
const colorForStatus = (label, i) =>
    STATUS_COLORS[String(label || "").toLowerCase()] || PALETTE[i % PALETTE.length];

// ── Small helpers ───────────────────────────────────────────────────────────
const fmtInt = (v) => Number(v || 0).toLocaleString();
const fmtDec = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);
const titleCase = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Draws a big number / caption in the middle of a doughnut chart.
const centerTextPlugin = {
    id: "centerText",
    afterDraw(chart) {
        const cfg = chart.options?.plugins?.centerText;
        if (!cfg || !cfg.text) return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const x = (chartArea.left + chartArea.right) / 2;
        const y = (chartArea.top + chartArea.bottom) / 2;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = cfg.color || "#1b3022";
        ctx.font = `800 ${cfg.fontSize || 24}px Inter, system-ui, sans-serif`;
        ctx.fillText(cfg.text, x, y - (cfg.subtext ? 8 : 0));
        if (cfg.subtext) {
            ctx.fillStyle = cfg.subtextColor || "#8aa39a";
            ctx.font = "600 10px Inter, system-ui, sans-serif";
            ctx.fillText(cfg.subtext, x, y + 14);
        }
        ctx.restore();
    },
};
ChartJS.register(centerTextPlugin);

const baseTooltip = {
    backgroundColor: "#1b3022",
    padding: 10,
    cornerRadius: 8,
    displayColors: false,
    titleFont: { size: 12, weight: "700" },
    bodyFont: { size: 12 },
};

const StatRow = ({ label, value, accent, strong }) => (
    <div className="ov-stat-row">
        <span className="ov-stat-label">{label}</span>
        <span className="ov-stat-value" style={{ ...(accent ? { color: accent } : {}), fontWeight: strong ? 800 : 700 }}>
            {value}
        </span>
    </div>
);

const Panel = ({ title, icon, accent, action, children, style }) => (
    <div className="ov-panel" style={style}>
        <div className="ov-panel-head">
            <div className="ov-panel-title">
                <span className="ov-panel-icon" style={{ background: `${accent}1a`, color: accent }}>{icon}</span>
                {title}
            </div>
            {action}
        </div>
        {children}
    </div>
);

const AgroDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get("/dashboard/agristats.php");
            setStats(res.data);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to load dashboard stats.";
            setError(msg);
            message.error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const header = (
        <>
            <Headerdiv />
            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { href: '/farmoverview', title: 'Farm Overview' },
                                { title: 'Dashboard' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <DashboardOutlined /> Farm Overview
                        </h1>
                        <p className="fluid-page-subtitle">
                            A real-time snapshot of crops, harvesting, production and operations
                        </p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <Space>
                            <Typography.Text className="ov-header-updated">
                                {stats ? `Updated ${dayjs().format("HH:mm")}` : "Loading..."}
                            </Typography.Text>
                            <Tooltip title="Refresh data">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={fetchStats}
                                    loading={loading}
                                    className="ov-refresh-btn"
                                />
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>
            </div>
        </>
    );

    if (loading && !stats) {
        return (
            <Layout className="fluid-page">
                {header}
                <Content className="fluid-content">
                    <div className="ov-loading">
                        <div style={{ textAlign: "center" }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 12, color: "#617d6a", fontWeight: 600 }}>Loading your farm overview...</div>
                        </div>
                    </div>
                </Content>
                <Footerdiv />
            </Layout>
        );
    }

    if (!stats) {
        return (
            <Layout className="fluid-page">
                {header}
                <Content className="fluid-content">
                    <div className="ov-panel">
                        <Alert
                            type="error"
                            showIcon
                            message="Could not load the farm overview"
                            description={error}
                            action={<Button size="small" icon={<ReloadOutlined />} onClick={fetchStats}>Retry</Button>}
                        />
                    </div>
                </Content>
                <Footerdiv />
            </Layout>
        );
    }

    const counts = stats.counts || {};
    const harvest = stats.harvest || {};
    const production = stats.production || {};
    const expenses = stats.expenses || {};
    const inventory = stats.inventory || {};
    const maintenance = stats.maintenance || stats.maintenance_stats || {};
    const plantsByStatus = stats.plants_by_status || [];
    const plantsByCategory = stats.plants_by_category || [];
    const upcomingMaintenance = stats.upcoming_maintenance || [];
    const lowStock = inventory.low_stock || [];

    const maintenancePending = maintenance.pending ?? 0;
    const maintenanceCompleted = maintenance.completed ?? 0;
    const maintenanceOverdue = maintenance.overdue ?? 0;
    const maintenanceTotal = maintenancePending + maintenanceCompleted;

    // ── KPI grid ────────────────────────────────────────────────────────────
    const kpis = [
        { label: "Total Lands", value: fmtInt(counts.lands), hint: `${fmtInt(counts.sections)} sections`, icon: <GlobalOutlined />, color: "#45a186" },
        { label: "Sections", value: fmtInt(counts.sections), hint: `${fmtInt(counts.fields)} fields`, icon: <AppstoreOutlined />, color: "#3b82f6" },
        { label: "Fields", value: fmtInt(counts.fields), hint: `${fmtInt(counts.plant_categories)} plant types`, icon: <BuildOutlined />, color: "#8b5cf6" },
        { label: "Active Plants", value: fmtInt(counts.plants), hint: `${plantsByStatus.length} status groups`, icon: <ExperimentOutlined />, color: "#14b8a6" },
        { label: "Harvest Batches", value: fmtInt(harvest.total_batches), hint: `${fmtInt(harvest.ongoing)} ongoing`, icon: <ShoppingOutlined />, color: "#f59e0b" },
        { label: "Production Batches", value: fmtInt(production.total_batches), hint: `${fmtInt(production.ongoing)} ongoing`, icon: <ThunderboltOutlined />, color: "#ef6b2e" },
        { label: "Inventory Items", value: fmtInt(inventory.total_items), hint: `${fmtInt(inventory.low_stock_count)} low stock`, icon: <ContainerOutlined />, color: "#0ea5e9" },
        { label: "Pending Maintenance", value: fmtInt(maintenancePending), hint: `${fmtInt(maintenanceOverdue)} overdue`, icon: <ToolOutlined />, color: "#f43f5e" },
    ];

    // ── Chart data ──────────────────────────────────────────────────────────
    const statusTotal = plantsByStatus.reduce((a, s) => a + Number(s.value || 0), 0);
    const statusData = {
        labels: plantsByStatus.map((s) => titleCase(s.label)),
        datasets: [{
            data: plantsByStatus.map((s) => Number(s.value || 0)),
            backgroundColor: plantsByStatus.map((s, i) => colorForStatus(s.label, i)),
            borderWidth: 0,
            hoverOffset: 8,
            spacing: 3,
            borderRadius: 8,
        }],
    };
    const statusOptions = {
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
            legend: { display: false },
            centerText: { text: fmtInt(statusTotal), subtext: "TOTAL PLANTS" },
            tooltip: { ...baseTooltip, callbacks: { label: (c) => ` ${c.label}: ${fmtInt(c.parsed)}` } },
        },
    };

    const categoryData = {
        labels: plantsByCategory.map((c) => c.label),
        datasets: [{
            label: "Plants",
            data: plantsByCategory.map((c) => Number(c.value || 0)),
            backgroundColor: plantsByCategory.map((_, i) => PALETTE[i % PALETTE.length]),
            borderRadius: 8,
            borderSkipped: false,
            maxBarThickness: 26,
        }],
    };
    const categoryOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { ...baseTooltip, callbacks: { label: (c) => ` ${fmtInt(c.parsed.y)} plants` } },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: "#1b3022", font: { weight: "600" } },
            },
            y: {
                beginAtZero: true,
                grid: { color: "rgba(27,48,34,0.06)" },
                ticks: { precision: 0, color: "#8aa39a" },
            },
        },
    };

    const maintCompletedPct = pct(maintenanceCompleted, maintenanceTotal);
    const maintData = {
        labels: ["Completed", "Pending"],
        datasets: [{
            data: [maintenanceCompleted, maintenancePending],
            backgroundColor: ["#45a186", "#ffa000"],
            borderWidth: 0,
            hoverOffset: 8,
            spacing: 3,
            borderRadius: 8,
        }],
    };
    const maintOptions = {
        maintainAspectRatio: false,
        cutout: "72%",
        plugins: {
            legend: { display: false },
            centerText: { text: `${maintCompletedPct}%`, subtext: "COMPLETED" },
            tooltip: { ...baseTooltip, callbacks: { label: (c) => ` ${c.label}: ${fmtInt(c.parsed)}` } },
        },
    };

    return (
        <Layout className="fluid-page">
            {header}

            <Content className="fluid-content">
                {/* KPI grid */}
                <Row gutter={[16, 16]}>
                    {kpis.map((k) => (
                        <Col xs={12} sm={12} md={8} lg={6} xl={3} key={k.label}>
                            <div className="ov-kpi" style={{ "--kpi": k.color }}>
                                <div className="ov-kpi-icon">{k.icon}</div>
                                <div className="ov-kpi-body">
                                    <div className="ov-kpi-value">{k.value}</div>
                                    <div className="ov-kpi-label">{k.label}</div>
                                    <div className="ov-kpi-hint">{k.hint}</div>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>

                {/* Distribution row */}
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} lg={8}>
                        <Panel
                            title="Plant Health"
                            icon={<ExperimentOutlined />}
                            accent="#45a186"
                            action={<span className="ov-panel-sub">{fmtInt(statusTotal)} plants</span>}
                            style={{ height: "100%" }}
                        >
                            {statusTotal > 0 ? (
                                <>
                                    <div style={{ height: 210 }}>
                                        <Doughnut data={statusData} options={statusOptions} />
                                    </div>
                                    <div className="ov-legend">
                                        {plantsByStatus.map((s, i) => (
                                            <span className="ov-legend-item" key={s.label}>
                                                <i style={{ background: colorForStatus(s.label, i) }} />
                                                {titleCase(s.label)}
                                                <b>{fmtInt(s.value)}</b>
                                            </span>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No plants recorded" style={{ padding: "40px 0" }} />
                            )}
                        </Panel>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Panel
                            title="Plants by Category"
                            icon={<AppstoreOutlined />}
                            accent="#3b82f6"
                            action={<span className="ov-panel-sub">{plantsByCategory.length} categories</span>}
                            style={{ height: "100%" }}
                        >
                            {plantsByCategory.length > 0 ? (
                                <div style={{ height: 210 }}>
                                    <Bar data={categoryData} options={categoryOptions} />
                                </div>
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No plant categories" style={{ padding: "40px 0" }} />
                            )}
                        </Panel>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Panel
                            title="Maintenance Progress"
                            icon={<ToolOutlined />}
                            accent="#ffa000"
                            action={<span className="ov-panel-sub">{fmtInt(maintenanceTotal)} tasks</span>}
                            style={{ height: "100%" }}
                        >
                            {maintenanceTotal > 0 ? (
                                <>
                                    <div style={{ height: 210 }}>
                                        <Doughnut data={maintData} options={maintOptions} />
                                    </div>
                                    <div className="ov-legend">
                                        <span className="ov-legend-item"><i style={{ background: "#45a186" }} />Completed<b>{fmtInt(maintenanceCompleted)}</b></span>
                                        <span className="ov-legend-item"><i style={{ background: "#ffa000" }} />Pending<b>{fmtInt(maintenancePending)}</b></span>
                                        {maintenanceOverdue > 0 && (
                                            <span className="ov-legend-item"><i style={{ background: "#f43f5e" }} />Overdue<b>{fmtInt(maintenanceOverdue)}</b></span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No maintenance tasks" style={{ padding: "40px 0" }} />
                            )}
                        </Panel>
                    </Col>
                </Row>

                {/* Harvest + Production */}
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} lg={12}>
                        <Panel
                            title="Harvest Overview"
                            icon={<ShoppingOutlined />}
                            accent="#f59e0b"
                            action={
                                <Button type="link" size="small" className="ov-link" onClick={() => navigate("/harvesting/harvest-batches")}>
                                    View <ArrowRightOutlined />
                                </Button>
                            }
                            style={{ height: "100%" }}
                        >
                            <Row gutter={16} align="middle">
                                <Col xs={24} sm={9} style={{ textAlign: "center" }}>
                                    <Progress
                                        type="dashboard"
                                        percent={pct(harvest.collected_kg, harvest.target_kg)}
                                        strokeColor={{ "0%": "#ffd166", "100%": "#f59e0b" }}
                                        trailColor="#f2f5f3"
                                        format={(p) => <span className="ov-ring-value">{p}%</span>}
                                    />
                                    <div className="ov-ring-caption">of target collected</div>
                                </Col>
                                <Col xs={24} sm={15}>
                                    <StatRow label="Collected" value={`${fmtDec(harvest.collected_kg)} kg`} accent="#f59e0b" />
                                    <StatRow label="Target" value={`${fmtDec(harvest.target_kg)} kg`} />
                                    <StatRow label="Rejected" value={`${fmtDec(harvest.rejected_kg)} kg`} accent="#f43f5e" />
                                    <div className="ov-chip-row">
                                        <Tag color="blue">{fmtInt(harvest.ongoing)} ongoing</Tag>
                                        <Tag color="green">{fmtInt(harvest.completed)} completed</Tag>
                                        <Tag>{fmtInt(harvest.pending)} pending</Tag>
                                    </div>
                                </Col>
                            </Row>
                        </Panel>
                    </Col>

                    <Col xs={24} lg={12}>
                        <Panel
                            title="Production Overview"
                            icon={<ThunderboltOutlined />}
                            accent="#ef6b2e"
                            action={
                                <Button type="link" size="small" className="ov-link" onClick={() => navigate("/production/production-batches")}>
                                    View <ArrowRightOutlined />
                                </Button>
                            }
                            style={{ height: "100%" }}
                        >
                            <Row gutter={16} align="middle">
                                <Col xs={24} sm={9} style={{ textAlign: "center" }}>
                                    <Progress
                                        type="dashboard"
                                        percent={pct(production.actual_kg, production.target_kg)}
                                        strokeColor={{ "0%": "#ffb27a", "100%": "#ef6b2e" }}
                                        trailColor="#f2f5f3"
                                        format={(p) => <span className="ov-ring-value">{p}%</span>}
                                    />
                                    <div className="ov-ring-caption">of target produced</div>
                                </Col>
                                <Col xs={24} sm={15}>
                                    <StatRow label="Produced" value={`${fmtDec(production.actual_kg)} kg`} accent="#ef6b2e" />
                                    <StatRow label="QC Approved" value={`${fmtDec(production.approved_kg)} kg`} accent="#45a186" />
                                    <StatRow label="QC Rejected" value={`${fmtDec(production.rejected_kg)} kg`} accent="#f43f5e" />
                                    <div className="ov-chip-row">
                                        <Tag color="orange" icon={<ClockCircleOutlined />}>{fmtInt(production.pending_approval)} pending approval</Tag>
                                        <Tag color="gold" icon={<SafetyCertificateOutlined />}>{fmtInt(production.pending_qc)} pending QC</Tag>
                                        <Tag color="green" icon={<CheckCircleOutlined />}>{fmtInt(production.finished)} finished</Tag>
                                    </div>
                                </Col>
                            </Row>
                        </Panel>
                    </Col>
                </Row>

                {/* Operations row */}
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} lg={8}>
                        <Panel
                            title="Expenses & Payments"
                            icon={<DollarOutlined />}
                            accent="#8b5cf6"
                            action={
                                <Button type="link" size="small" className="ov-link" onClick={() => navigate("/expenses/list")}>
                                    View <ArrowRightOutlined />
                                </Button>
                            }
                            style={{ height: "100%" }}
                        >
                            <StatRow label="Total Expenses" value={`Rs. ${fmtMoney(expenses.total_amount)}`} strong />
                            <StatRow label="Paid" value={`Rs. ${fmtMoney(expenses.paid_amount)}`} accent="#45a186" />
                            <StatRow label="Pending Approval" value={fmtInt(expenses.pending_approval)} accent="#ffa000" />
                            <StatRow label="Approved Records" value={fmtInt(expenses.approved)} />
                            <div style={{ marginTop: 12 }}>
                                <Progress
                                    percent={pct(expenses.paid_amount, expenses.total_amount)}
                                    strokeColor={{ "0%": "#a78bfa", "100%": "#8b5cf6" }}
                                    trailColor="#f2f5f3"
                                    size="small"
                                />
                                <div className="ov-progress-caption">Payments released vs total expenses</div>
                            </div>
                        </Panel>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Panel
                            title="Inventory Health"
                            icon={<ContainerOutlined />}
                            accent="#0ea5e9"
                            action={
                                <Button type="link" size="small" className="ov-link" onClick={() => navigate("/inventory-items")}>
                                    View <ArrowRightOutlined />
                                </Button>
                            }
                            style={{ height: "100%" }}
                        >
                            <StatRow label="Stock Value" value={`Rs. ${fmtMoney(inventory.stock_value)}`} strong />
                            <StatRow label="Total Items" value={fmtInt(inventory.total_items)} />
                            <StatRow label="Units Available" value={fmtInt(inventory.total_qty)} />
                            <StatRow label="Low Stock Items" value={fmtInt(inventory.low_stock_count)} accent="#f43f5e" />
                            <div className="ov-divider" />
                            <div className="ov-subhead">Low stock alerts</div>
                            {lowStock.length > 0 ? (
                                lowStock.map((it) => (
                                    <div className="ov-list-item" key={it.invi_id}>
                                        <div className="ov-list-main">
                                            <span className="ov-list-title">{it.item_name}</span>
                                            <span className="ov-list-sub">{it.inv_code}</span>
                                        </div>
                                        <Tag color="red">{fmtInt(it.available_qty)} / {fmtInt(it.reorder_qty)}</Tag>
                                    </div>
                                ))
                            ) : (
                                <div className="ov-empty-inline"><CheckCircleOutlined /> All items above reorder level</div>
                            )}
                        </Panel>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Panel
                            title="Upcoming Maintenance"
                            icon={<ClockCircleOutlined />}
                            accent="#f43f5e"
                            action={
                                <Button type="link" size="small" className="ov-link" onClick={() => navigate("/maintenance")}>
                                    View <ArrowRightOutlined />
                                </Button>
                            }
                            style={{ height: "100%" }}
                        >
                            {upcomingMaintenance.length > 0 ? (
                                upcomingMaintenance.map((m, i) => {
                                    const due = m.target_date ? dayjs(m.target_date) : null;
                                    const days = due ? due.startOf("day").diff(dayjs().startOf("day"), "day") : null;
                                    const overdue = days !== null && days < 0;
                                    const soon = days !== null && days >= 0 && days <= 3;
                                    return (
                                        <div className="ov-list-item" key={`${m.scope}-${m.main_id}-${i}`}>
                                            <div className="ov-list-main">
                                                <span className="ov-list-title">{m.main_title || "Maintenance task"}</span>
                                                <span className="ov-list-sub">{m.scope} · {due ? due.format("YYYY-MM-DD") : "No date"}</span>
                                            </div>
                                            <Tag color={overdue ? "red" : soon ? "orange" : "default"} icon={overdue ? <AlertOutlined /> : <ClockCircleOutlined />}>
                                                {days === null ? "—" : overdue ? `${Math.abs(days)}d overdue` : days === 0 ? "Due today" : `${days}d left`}
                                            </Tag>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="ov-empty-inline"><CheckCircleOutlined /> No pending maintenance</div>
                            )}
                        </Panel>
                    </Col>
                </Row>
            </Content>

            <Footerdiv />

            <style>{`
                .ov-loading {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 60vh;
                }
                .ov-header-updated {
                    color: var(--fluid-accent) !important;
                    font-size: 12px;
                    opacity: .85;
                }
                .ov-refresh-btn {
                    background: rgba(255,255,255,.16) !important;
                    border: 1px solid rgba(255,255,255,.35) !important;
                    color: #fff !important;
                    border-radius: 12px !important;
                }
                .ov-refresh-btn:hover {
                    background: rgba(255,255,255,.28) !important;
                }

                /* KPI cards */
                .ov-kpi {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    background: #fff;
                    border-radius: 18px;
                    padding: 16px 18px;
                    box-shadow: 0 4px 20px rgba(27,48,34,.06);
                    border: 1px solid rgba(232,245,233,.9);
                    overflow: hidden;
                    height: 100%;
                    transition: transform .25s ease, box-shadow .25s ease;
                }
                .ov-kpi::after {
                    content: "";
                    position: absolute;
                    top: -30px;
                    right: -30px;
                    width: 110px;
                    height: 110px;
                    border-radius: 50%;
                    background: var(--kpi);
                    opacity: .07;
                }
                .ov-kpi:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 16px 34px rgba(27,48,34,.14);
                }
                .ov-kpi-icon {
                    width: 46px;
                    height: 46px;
                    flex: none;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    color: #fff;
                    background: var(--kpi);
                    box-shadow: 0 10px 20px -8px var(--kpi);
                    z-index: 1;
                }
                .ov-kpi-body { min-width: 0; z-index: 1; }
                .ov-kpi-value {
                    font-size: 26px;
                    font-weight: 800;
                    color: #1b3022;
                    line-height: 1.1;
                    letter-spacing: -.6px;
                }
                .ov-kpi-label {
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #617d6a;
                    text-transform: uppercase;
                    letter-spacing: .6px;
                    margin-top: 2px;
                }
                .ov-kpi-hint {
                    font-size: 11px;
                    color: #8aa39a;
                    margin-top: 2px;
                }

                /* Panels */
                .ov-panel {
                    background: #fff;
                    border-radius: 20px;
                    padding: 18px 20px;
                    box-shadow: 0 4px 20px rgba(27,48,34,.06);
                    border: 1px solid rgba(232,245,233,.9);
                    display: flex;
                    flex-direction: column;
                }
                .ov-panel-head {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 14px;
                    gap: 8px;
                }
                .ov-panel-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 15px;
                    font-weight: 700;
                    color: #1b3022;
                }
                .ov-panel-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                }
                .ov-panel-sub { font-size: 12px; color: #8aa39a; font-weight: 600; }

                /* Legends + list rows */
                .ov-legend {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px 16px;
                    margin-top: 14px;
                }
                .ov-legend-item {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    color: #617d6a;
                    font-weight: 600;
                }
                .ov-legend-item i {
                    width: 10px;
                    height: 10px;
                    border-radius: 3px;
                    display: inline-block;
                }
                .ov-legend-item b { color: #1b3022; }

                .ov-stat-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 9px 12px;
                    border-radius: 12px;
                    transition: background .2s ease;
                }
                .ov-stat-row:nth-child(odd) { background: #f7faf8; }
                .ov-stat-label { font-size: 12.5px; color: #617d6a; font-weight: 600; }
                .ov-stat-value { font-size: 14px; color: #1b3022; }

                .ov-ring-value { font-size: 20px; font-weight: 800; color: #1b3022; }
                .ov-ring-caption { font-size: 11px; color: #8aa39a; font-weight: 600; margin-top: 2px; }
                .ov-progress-caption { font-size: 11px; color: #8aa39a; margin-top: 4px; }

                .ov-chip-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    margin-top: 12px;
                }
                .ov-chip-row .ant-tag { margin: 0; border-radius: 8px; font-weight: 600; }

                .ov-divider {
                    height: 1px;
                    background: #eef4f0;
                    margin: 14px 0 10px;
                }
                .ov-subhead {
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: .6px;
                    text-transform: uppercase;
                    color: #8aa39a;
                    margin-bottom: 6px;
                }
                .ov-list-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    padding: 9px 0;
                    border-bottom: 1px dashed #eef4f0;
                }
                .ov-list-item:last-child { border-bottom: none; }
                .ov-list-main { display: flex; flex-direction: column; min-width: 0; }
                .ov-list-title {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1b3022;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .ov-list-sub { font-size: 11px; color: #8aa39a; }
                .ov-empty-inline {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #617d6a;
                    font-size: 12.5px;
                    padding: 14px 0;
                }
                .ov-link {
                    color: var(--fluid-primary) !important;
                    font-weight: 600 !important;
                    padding: 0 !important;
                    height: auto !important;
                }
                .ov-panel .ant-empty-description { color: #8aa39a; }
            `}</style>
        </Layout>
    );
};

export default AgroDashboard;
